/**
 * Metadata Schema Definitions for RE Platform v4 MVP
 *
 * This file defines the structure for all metadata used in the dynamic form system:
 * - Template definitions (form layouts and sections)
 * - Dataset definitions (data structure schemas)
 * - Field definitions (individual form fields)
 * - Field enrichments (external API integrations)
 */

// Field Types supported by the dynamic form system
export const FIELD_TYPES = {
  // Basic Input Types
  TEXT: "text",
  EMAIL: "email",
  NUMBER: "number",
  CURRENCY: "currency",
  PERCENTAGE: "percentage",
  PHONE: "phone",
  URL: "url",
  PASSWORD: "password",

  // Text Areas and Rich Content
  TEXTAREA: "textarea",
  RICH_TEXT: "rich_text",

  // Selection Types
  SELECT: "select",
  MULTI_SELECT: "multi_select",
  RADIO: "radio",
  CHECKBOX: "checkbox",
  BOOLEAN: "boolean",

  // Date and Time
  DATE: "date",
  TIME: "time",
  DATETIME: "datetime",
  DATE_RANGE: "date_range",

  // File Handling
  FILE: "file",
  IMAGE: "image",
  DOCUMENT: "document",

  // Specialized Types
  ADDRESS: "address",
  POSTCODE: "postcode",
  COMPANY_NUMBER: "company_number",
  PROPERTY_REF: "property_ref",

  // Calculated/Display Only
  CALCULATED: "calculated",
  DISPLAY_ONLY: "display_only",
  SEPARATOR: "separator",
};

// Validation Rule Types
export const VALIDATION_TYPES = {
  REQUIRED: "required",
  MIN_LENGTH: "min_length",
  MAX_LENGTH: "max_length",
  MIN_VALUE: "min_value",
  MAX_VALUE: "max_value",
  PATTERN: "pattern",
  EMAIL: "email",
  URL: "url",
  CUSTOM: "custom",
};

// Field Enrichment Sources
export const ENRICHMENT_SOURCES = {
  COMPANIES_HOUSE: "companies_house",
  LANDWORTH: "landworth",
  POSTCODE_API: "postcode_api",
  CUSTOM_API: "custom_api",
};

// Template Schema - Defines the overall form structure
export const TEMPLATE_SCHEMA = {
  id: "string", // Unique template identifier
  name: "string", // Human-readable template name
  description: "string", // Template description
  version: "string", // Template version (e.g., '1.0.0')
  category: "string", // Template category (e.g., 'project', 'deal', 'valuation')

  // Metadata
  metadata: {
    created_at: "datetime",
    updated_at: "datetime",
    created_by: "string",
    tags: ["string"], // Template tags for organization
    status: "string", // 'draft', 'active', 'deprecated'
  },

  // Form Layout Configuration
  layout: {
    columns: "number", // Number of columns (1, 2, 3)
    sections: [
      {
        id: "string",
        title: "string",
        description: "string",
        collapsible: "boolean",
        columns: "number", // Override global columns for this section
        fields: ["string"], // Array of field IDs
        conditional: {
          field: "string", // Field ID to watch
          operator: "string", // 'equals', 'not_equals', 'contains', etc.
          value: "any", // Value to compare against
        },
      },
    ],
  },

  // Associated datasets
  datasets: ["string"], // Array of dataset IDs

  // Template-level settings
  settings: {
    auto_save: "boolean",
    save_interval: "number", // Auto-save interval in seconds
    show_progress: "boolean",
    allow_partial_save: "boolean",
    enable_enrichment: "boolean",
    enrichment_trigger: "string", // 'on_blur', 'on_change', 'manual'
  },
};

// Dataset Schema - Defines data structure and related fields
export const DATASET_SCHEMA = {
  id: "string", // Unique dataset identifier
  name: "string", // Human-readable dataset name
  description: "string", // Dataset description
  version: "string", // Dataset version

  // Metadata
  metadata: {
    created_at: "datetime",
    updated_at: "datetime",
    created_by: "string",
    tags: ["string"],
    status: "string",
  },

  // Data Structure
  fields: ["string"], // Array of field IDs

  // Relationships to other datasets
  relationships: [
    {
      type: "string", // 'one_to_one', 'one_to_many', 'many_to_many'
      dataset: "string", // Related dataset ID
      foreign_key: "string", // Field ID that links to related dataset
      cascade_delete: "boolean",
    },
  ],

  // Dataset-level validation rules
  validation: {
    unique_fields: [["string"]], // Arrays of field combinations that must be unique
    required_combinations: [["string"]], // Field combinations where at least one must be filled
    conditional_required: [
      {
        condition: {
          field: "string",
          operator: "string",
          value: "any",
        },
        required_fields: ["string"],
      },
    ],
  },

  // API endpoints for CRUD operations
  api: {
    base_url: "string",
    endpoints: {
      create: "string",
      read: "string",
      update: "string",
      delete: "string",
      list: "string",
    },
    auth: {
      type: "string", // 'bearer', 'api_key', 'basic'
      header: "string", // Header name for auth
    },
  },
};

// Field Schema - Defines individual form fields
export const FIELD_SCHEMA = {
  id: "string", // Unique field identifier
  name: "string", // Field name/key for data storage
  label: "string", // Display label
  description: "string", // Help text
  type: "string", // One of FIELD_TYPES

  // Basic Properties
  required: "boolean",
  disabled: "boolean",
  readonly: "boolean",
  hidden: "boolean",

  // Default and Placeholder
  default_value: "any",
  placeholder: "string",

  // Validation Rules
  validation: [
    {
      type: "string", // One of VALIDATION_TYPES
      value: "any", // Validation parameter
      message: "string", // Custom error message
    },
  ],

  // Field-specific Configuration
  config: {
    // For select/multi-select fields
    options: [
      {
        value: "any",
        label: "string",
        disabled: "boolean",
        group: "string", // Option group
      },
    ],

    // For number/currency fields
    min: "number",
    max: "number",
    step: "number",
    decimal_places: "number",
    currency_symbol: "string",

    // For text fields
    min_length: "number",
    max_length: "number",
    pattern: "string", // Regex pattern

    // For file fields
    accept: ["string"], // MIME types
    max_file_size: "number", // Bytes
    multiple: "boolean",

    // For date fields
    min_date: "string",
    max_date: "string",
    date_format: "string",

    // For calculated fields
    formula: "string", // JavaScript expression
    dependencies: ["string"], // Field IDs this calculation depends on

    // UI Configuration
    width: "string", // 'full', 'half', 'third', 'quarter'
    height: "number", // For textarea
    rows: "number", // For textarea
    columns: "number", // Field span in grid layout
  },

  // Field Enrichment Configuration
  enrichment: {
    enabled: "boolean",
    source: "string", // One of ENRICHMENT_SOURCES
    trigger: "string", // 'on_blur', 'on_change', 'manual'
    endpoint: "string", // API endpoint for enrichment
    mapping: {
      // Maps API response fields to form fields
      api_field: "form_field_id",
    },
    depends_on: ["string"], // Field IDs that must be filled before enrichment
    cache_duration: "number", // Cache enrichment results (seconds)
    auto_accept: "boolean", // Automatically accept enrichment results
  },

  // Conditional Logic
  conditional: {
    show_when: {
      field: "string",
      operator: "string",
      value: "any",
    },
    enable_when: {
      field: "string",
      operator: "string",
      value: "any",
    },
    require_when: {
      field: "string",
      operator: "string",
      value: "any",
    },
  },

  // Field Metadata
  metadata: {
    created_at: "datetime",
    updated_at: "datetime",
    created_by: "string",
    tags: ["string"],
    help_url: "string", // Link to documentation
    data_source: "string", // Where this field's data comes from
  },
};

// Field Enrichment Schema - Defines external API integrations
export const ENRICHMENT_SCHEMA = {
  id: "string", // Unique enrichment identifier
  name: "string", // Human-readable name
  description: "string",
  source: "string", // One of ENRICHMENT_SOURCES

  // API Configuration
  api: {
    base_url: "string",
    endpoint: "string",
    method: "string", // 'GET', 'POST'
    headers: {
      header_name: "string",
    },
    auth: {
      type: "string",
      key: "string",
      header: "string",
    },
    timeout: "number", // Request timeout in ms
  },

  // Request Configuration
  request: {
    // Parameters to send to API
    params: {
      param_name: {
        source: "string", // 'field', 'config', 'context'
        field: "string", // Field ID if source is 'field'
        value: "any", // Static value if source is 'config'
        transform: "string", // Optional transformation function
      },
    },
  },

  // Response Processing
  response: {
    // How to extract data from API response
    data_path: "string", // JSONPath to data (e.g., '$.data.company')

    // Field mappings
    mappings: [
      {
        source: "string", // API field path
        target: "string", // Form field ID
        transform: "string", // Optional transformation function
        default: "any", // Default value if API field is missing
      },
    ],

    // Error handling
    error_path: "string", // JSONPath to error message
    success_condition: "string", // JSONPath or condition for success
  },

  // Caching Configuration
  cache: {
    enabled: "boolean",
    duration: "number", // Cache duration in seconds
    key_fields: ["string"], // Fields to use for cache key
  },

  // Rate Limiting
  rate_limit: {
    requests_per_minute: "number",
    requests_per_hour: "number",
    requests_per_day: "number",
  },

  // Metadata
  metadata: {
    created_at: "datetime",
    updated_at: "datetime",
    created_by: "string",
    tags: ["string"],
    cost_per_request: "number", // Tracking API costs
    last_used: "datetime",
  },
};

// Master Metadata Schema - Combines all schemas
export const METADATA_SCHEMA = {
  version: "string", // Metadata schema version
  templates: [TEMPLATE_SCHEMA],
  datasets: [DATASET_SCHEMA],
  fields: [FIELD_SCHEMA],
  enrichments: [ENRICHMENT_SCHEMA],

  // Global Configuration
  config: {
    auto_save_interval: "number",
    enable_field_enrichment: "boolean",
    enable_offline_mode: "boolean",
    max_file_upload_size: "number",
    supported_file_types: ["string"],
    date_format: "string",
    currency_symbol: "string",
    decimal_separator: "string",
    thousand_separator: "string",
  },
};

// Schema validation helpers will be implemented in the validation service
export const SCHEMA_VALIDATORS = {
  template: (template) => {
    // Validate template against TEMPLATE_SCHEMA
    // Returns { valid: boolean, errors: string[] }
  },
  dataset: (dataset) => {
    // Validate dataset against DATASET_SCHEMA
  },
  field: (field) => {
    // Validate field against FIELD_SCHEMA
  },
  enrichment: (enrichment) => {
    // Validate enrichment against ENRICHMENT_SCHEMA
  },
};

export default {
  FIELD_TYPES,
  VALIDATION_TYPES,
  ENRICHMENT_SOURCES,
  TEMPLATE_SCHEMA,
  DATASET_SCHEMA,
  FIELD_SCHEMA,
  ENRICHMENT_SCHEMA,
  METADATA_SCHEMA,
  SCHEMA_VALIDATORS,
};
